import React, { useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import Label from "@/components/form/Label";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";
import { formatDateOnly, parseDateLocal } from "@/utils/formatters/dateUtils";

/** What the calendar prints in the visible box. */
const CALENDAR_DISPLAY_FORMAT = 'm-d-Y';
/** YYYY-MM-DD — what the caller passes in and gets back, unchanged. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isoToDate = (raw: string | number | undefined): Date | undefined => {
  if (raw === undefined || raw === '') return undefined;
  const trimmed = String(raw).trim();
  return ISO_DATE_PATTERN.test(trimmed) ? parseDateLocal(trimmed) : undefined;
};

/** The ISO date a flatpickr instance currently holds, or '' for no selection. */
const selectedIso = (instance: flatpickr.Instance): string => {
  const selected = instance.selectedDates[0];
  return selected ? formatDateOnly(selected) : '';
};

interface TextFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string; // Made optional
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string | React.ReactNode;
  // Back-compat aliases used by some screens
  helperText?: string | React.ReactNode;
  helpText?: string | React.ReactNode;
  className?: string;
  inputClassName?: string; // Additional classes for the input element
  type?: "text" | "password" | "search" | "number" | "date" | "time" | "datetime-local" | "email" | "tel" | "url";
  min?: string | number;
  max?: string | number;
  step?: string | number;
  inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
  maxLength?: number;
  disabled?: boolean;
  /**
   * Show the value but prevent editing (still legible, unlike `disabled` which
   * dims to 40%). Use for fields the user must SEE but not change — e.g. an email
   * locked to an invitation. The value still submits normally.
   */
  readOnly?: boolean;
  autoComplete?: string;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  /** Accessible name for fields that show no visible label (e.g. a search box
   *  whose purpose is carried by its icon and placeholder). */
  ariaLabel?: string;
  /**
   * Opt a `type="date"` field into the calendar (see the block comment below).
   * Off by default: the calendar replaces behaviour the browser's own date box
   * provides for free, so screens are moved across a batch at a time rather
   * than all at once. Without it a date field stays the plain typed box.
   */
  calendar?: boolean;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(({
  id,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  label, // Now optional
  placeholder = "",
  required = false,
  error = false,
  hint,
  helperText,
  helpText,
  className = "",
  inputClassName = "",
  type = "text",
  min,
  max,
  step,
  inputMode,
  maxLength,
  disabled = false,
  readOnly = false,
  autoComplete,
  prefix,
  suffix,
  ariaLabel,
  calendar = false,
}, ref) => {
  const describedById = ((helperText ?? helpText ?? hint) || (typeof error === 'string' && error))
    ? `${id}-description`
    : undefined;
  // Date fields show flatpickr's calendar instead of the browser's typed
  // mm/dd/yyyy box. Two invariants keep the blast radius at zero, and both were
  // learned the expensive way in review:
  //
  //   1. The DOM value stays YYYY-MM-DD. Code around this app reads a date
  //      input straight off the DOM — `document.querySelector('input[name=
  //      "usasfExpirationDate"]').value` in the athlete form is one of five —
  //      so a visible box holding "08-18-2026" writes that string into a
  //      compliance record. React owns a hidden input carrying `name` and the
  //      ISO value; flatpickr owns the visible one. A hidden input React itself
  //      renders stays hidden, unlike flatpickr's own altInput, which React
  //      un-hides on the next update and leaves two boxes stacked.
  //
  //   2. The calendar is the ONLY way to set the date (`allowInput: false`).
  //      Free typing gave flatpickr several commit paths, and each one that
  //      skipped `onChange` left the box showing a date the form did not have:
  //      clicking away rather than tabbing, pressing Escape, and a half-typed
  //      or impossible entry that its parser quietly rounded into a real day.
  //      Removing typing removes the whole category rather than one case at a
  //      time — and typing by hand is the thing this change exists to replace.
  //
  // readOnly fields keep the plain controlled input — there is nothing to pick.
  const useCalendar = calendar && type === 'date' && !readOnly;

  // Only calendar mode is excluded: there flatpickr owns the DOM value, so
  // reading it back would fight the widget. Every OTHER date field keeps the
  // Safari reconcile below untouched — this condition was `&& readOnly` for a
  // while, which silently switched it off for all ~75 fields that had not
  // opted in, since an ordinary date field is not readOnly.
  const shouldReconcileNativeValue = (inputType: TextFieldProps['type']) =>
    (inputType === 'date' && !useCalendar) || inputType === 'time' || inputType === 'datetime-local';

  // Requirements Summary: Native date/time controls can update their DOM value
  // without keeping React state in sync, so reconcile these inputs explicitly.
  const reconcileNativeValue = (
    e: React.FocusEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>
  ) => {
    if (!shouldReconcileNativeValue(type)) return;

    const targetEl = e.currentTarget;
    const domValue = targetEl.value;
    if (typeof value === 'string' && domValue === value) return;

    const syntheticEvent = {
      target: targetEl,
      currentTarget: targetEl,
      type: 'change',
      bubbles: true,
      cancelable: false,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: false,
      nativeEvent: e.nativeEvent,
      preventDefault: () => {},
      isDefaultPrevented: () => false,
      stopPropagation: () => {},
      isPropagationStopped: () => false,
      persist: () => {},
      timeStamp: e.timeStamp,
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    flushSync(() => {
      onChange(syntheticEvent);
    });
  };

  // Handle date input changes with year validation
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'date') {
      const dateValue = e.target.value;

      // Check if the date format is valid (YYYY-MM-DD)
      if (dateValue) {
        const parts = dateValue.split('-');
        if (parts.length === 3) {
          const year = parts[0];

          // Limit year to 4 digits
          if (year.length > 4) {
            // Truncate year to 4 digits
            const truncatedYear = year.slice(0, 4);
            const correctedDateValue = `${truncatedYear}-${parts[1]}-${parts[2]}`;

            // Create a new event with the corrected value
            const correctedEvent = {
              ...e,
              target: {
                ...e.target,
                value: correctedDateValue,
              }
            } as React.ChangeEvent<HTMLInputElement>;

            onChange(correctedEvent);
            return;
          }
        }
      }
    }

    // Call the original onChange handler
    onChange(e);
  };

  // Safari fix: Safari's native date picker may not fire `input` events (React's
  // onChange) as the user types segments — the DOM value updates but React state
  // stays stale. On blur, reconcile React state from the DOM value so the form
  // sees the typed date even if the user never tabbed out before clicking Next.
  // flushSync forces the state update to commit synchronously before blur()
  // returns, so validation reading from refs sees the updated value.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    reconcileNativeValue(e);
    onBlur?.(e);
  };
  
  // Build input classes using TailAdmin styling
  const getInputClasses = () => {
    let baseClasses = "h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30";
    
    if (disabled) {
      return `${baseClasses} text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
    } else if (readOnly) {
      // Legible (not dimmed) but clearly locked — the value matters here.
      return `${baseClasses} text-gray-700 border-gray-300 bg-gray-50 cursor-default dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700`;
    } else if (error) {
      return `${baseClasses} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800`;
    } else {
      return `${baseClasses} bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800`;
    }
  };


  const inputRef = useRef<HTMLInputElement | null>(null);
  const fpRef = useRef<flatpickr.Instance | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  // The last ISO date this field told the caller about. Every commit path is
  // reconciled against it, so a date reaches the caller exactly once.
  const lastReportedRef = useRef(value || '');

  // Tell the caller the date changed. A plain object, not the DOM node:
  // callers only read target.value and target.name, and cloning the node
  // invokes getters that throw.
  const reportIso = useCallback((iso: string) => {
    if (iso === lastReportedRef.current) return;
    lastReportedRef.current = iso;
    onChangeRef.current({
      target: { value: iso, name, id, type: 'date' },
      currentTarget: { value: iso, name, id, type: 'date' },
      type: 'change',
      preventDefault: () => {},
      stopPropagation: () => {},
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  }, [id, name]);

  const setInputRef = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  }, [ref]);

  useEffect(() => {
    const el = inputRef.current;
    if (!useCalendar || !el) return;

    const instance = flatpickr(el, {
      dateFormat: CALENDAR_DISPLAY_FORMAT,
      allowInput: false,
      disableMobile: true,
      monthSelectorType: 'static',
      static: false,
      appendTo: typeof document !== 'undefined' ? document.body : undefined,
      defaultDate: isoToDate(valueRef.current),
      onChange: (selectedDates) => {
        reportIso(selectedDates.length > 0 ? formatDateOnly(selectedDates[0]) : '');
      },
      // A belt-and-braces reconcile. flatpickr has more than one path that can
      // settle a date without firing onChange; onClose runs on every close, and
      // reportIso's dedupe keeps an ordinary pick from reporting twice.
      onClose: (_selectedDates, _dateStr, instance) => {
        reportIso(selectedIso(instance));
      },
    });

    fpRef.current = instance;
    return () => {
      instance.destroy();
      fpRef.current = null;
    };
  }, [useCalendar, id, name, reportIso]);

  // Keep the calendar in step when the form sets the date itself — defaults on
  // open, a reset, or loading an existing record. Compare on the ISO value so a
  // no-op re-render never clobbers what the user just picked.
  useEffect(() => {
    const instance = fpRef.current;
    if (!instance) return;
    if (selectedIso(instance) === (value || '')) return;
    lastReportedRef.current = value || '';
    // A Date object, not the YYYY-MM-DD string: flatpickr would otherwise try to
    // parse the string with the display format and silently fail.
    instance.setDate(isoToDate(value) ?? '', false);
  }, [value]);

  // Limits are applied here rather than at construction, and the two cases mean
  // opposite things:
  //
  //   FIRST application — the stored date is the truth. A record whose date sits
  //   outside this screen's limits still has that date, so admit it and show it.
  //   Blanking the box would tell the person the record is empty when it is not,
  //   and reporting it away would wipe the record by merely opening the screen.
  //   "First" means the first time a limit actually EXISTS, not the first render:
  //   limits often arrive a beat later, once the thing that sets them has loaded.
  //
  //   LATER change — the new limit is the truth. flatpickr drops a date its
  //   limits now exclude and blanks the box with no change event
  //   (index.js:1274-1278), so this must report, or the form would keep — and
  //   save — a date the screen no longer shows.
  const boundsAppliedRef = useRef(false);
  const hadMinRef = useRef(false);
  const hadMaxRef = useRef(false);
  useEffect(() => {
    const instance = fpRef.current;
    if (!instance) return;

    if (!boundsAppliedRef.current) {
      const stored = valueRef.current || '';
      const belowMin = stored !== '' && min !== undefined && stored < String(min);
      const aboveMax = stored !== '' && max !== undefined && stored > String(max);
      instance.set('minDate', belowMin ? isoToDate(stored) : isoToDate(min));
      instance.set('maxDate', aboveMax ? isoToDate(stored) : isoToDate(max));
      boundsAppliedRef.current = min !== undefined || max !== undefined;
      hadMinRef.current = min !== undefined;
      hadMaxRef.current = max !== undefined;
      lastReportedRef.current = selectedIso(instance);
      return;
    }

    // A limit that appears for the first time on a LATER render is still a
    // first limit for that side, so it must not evict a stored date either.
    // Only a side that already had a limit is a genuine narrowing.
    const stored = valueRef.current || '';
    const minIsNew = min !== undefined && !hadMinRef.current;
    const maxIsNew = max !== undefined && !hadMaxRef.current;
    hadMinRef.current = hadMinRef.current || min !== undefined;
    hadMaxRef.current = hadMaxRef.current || max !== undefined;

    const admitBelow = minIsNew && stored !== '' && stored < String(min);
    const admitAbove = maxIsNew && stored !== '' && stored > String(max);
    instance.set('minDate', admitBelow ? isoToDate(stored) : isoToDate(min));
    instance.set('maxDate', admitAbove ? isoToDate(stored) : isoToDate(max));
    reportIso(selectedIso(instance));
  }, [min, max, reportIso]);

  useEffect(() => {
    fpRef.current?.set('clickOpens', !disabled);
  }, [disabled]);

  return (
    <div className={`${className ? `${className} w-full` : "w-full"} overflow-visible`}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      <div className="relative">
        {prefix && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-gray-500 dark:text-gray-400">
            {prefix}
          </div>
        )}
        {/* React owns this one: `name` plus the YYYY-MM-DD value, so a native
            submit and every `querySelector('input[name=...]').value` in the app
            read exactly what they read before this field became a calendar.
            It is an off-screen text input rather than type="hidden" because the
            browser skips validation on a hidden input, and `required` on a date
            field would silently stop blocking an empty submit. It is also
            `disabled` in step with the field, or a greyed-out date would submit
            a value the native box left out. */}
        {useCalendar && (
          <input
            type="text"
            name={name}
            value={value}
            onChange={() => {}}
            required={required}
            disabled={disabled}
            tabIndex={-1}
            aria-hidden="true"
            // A browser tries to focus the invalid control and show its bubble
            // there. This one cannot be seen, so the bubble goes nowhere and the
            // form looks stuck for no reason. Send the person to the box they
            // can actually see instead; the submit is still blocked, and the
            // screen's own error copy is what explains why.
            onInvalid={(event) => {
              event.preventDefault();
              inputRef.current?.focus();
            }}
            className="absolute h-0 w-0 border-0 p-0 opacity-0"
          />
        )}
        <input
          ref={setInputRef}
          id={id}
          name={useCalendar ? undefined : name}
          type={useCalendar ? 'text' : type}
          placeholder={useCalendar && !placeholder ? 'MM-DD-YYYY' : placeholder}
          // Uncontrolled in calendar mode: flatpickr owns the DOM value, and the
          // effect above pushes the caller's value in. Handing React `value` as
          // well makes the two fight over every keystroke.
          {...(useCalendar
            ? {}
            : { value, onChange: handleDateChange, onInput: reconcileNativeValue })}
          onBlur={handleBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          required={useCalendar ? undefined : required}
          // Only in calendar mode, where the real `required` lives on the
          // off-screen carrier and this box would otherwise announce nothing.
          // A plain date field keeps native `required` and must stay identical
          // to main, attribute for attribute.
          aria-required={useCalendar && required ? true : undefined}
          disabled={disabled}
          readOnly={readOnly || useCalendar}
          min={useCalendar ? undefined : min?.toString()}
          max={useCalendar ? undefined : max?.toString()}
          step={step?.toString()}
          inputMode={inputMode}
          maxLength={maxLength}
          autoComplete={useCalendar ? 'off' : autoComplete}
          aria-invalid={!!error}
          aria-label={ariaLabel}
          aria-describedby={describedById}
          className={`${getInputClasses()} ${inputClassName} ${prefix ? 'pl-12' : ''} ${suffix || useCalendar ? 'pr-12' : ''} ${useCalendar && !disabled ? 'cursor-pointer' : ''}`.trim()}
        />
        {(suffix || useCalendar) && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500 dark:text-gray-400">
            {suffix ?? <GoogleMaterialIcon name="calendar_month" size="md" />}
          </div>
        )}
      </div>
      {((helperText ?? helpText ?? hint) || (typeof error === 'string' && error)) && (
        <p
          id={describedById}
          className={`mt-1.5 text-xs ${
            error
              ? "text-error-500"
              : "text-gray-500"
          }`}
        >
          {typeof error === 'string' ? error : (helperText ?? helpText ?? hint)}
        </p>
      )}
    </div>
  );
});

TextField.displayName = 'TextField';

export default TextField;
